"use client";

import { IconCalendarOutline18, IconChevronDownOutline18, IconCircleInfoOutline18 } from "nucleo-ui-outline-18";
import { useEffect, useMemo, useRef, useState } from "react";
import { formatRRule } from "@/components/ui/workflow-step-row/utils/formatters";
import JsonInput from "../../form-fields/JsonInput";
import { getArchetypeConfig } from "./registry";
import type { TriggerArchetypeProps } from "./types";

/**
 * SchemaDrivenForm archetype — renders a form for the workflow's inputSchema
 * fields if any exist, or a "click to run" message if not. Both modes expose
 * an "Advanced (payload)" disclosure for raw JSON override.
 *
 * Used for the "single-trigger-button" family — manual, cronjob, schedule —
 * where the test data is either user-defined inputs or empty.
 *
 * Form fields are always re-derived from the *current* inputSchema, so editing
 * the workflow's input schema while the modal is open does not leave stale
 * fields behind.
 *
 * TODO (B3-3608): replace the text-input rendering for `string` fields with
 * rich pickers (slack-channel, wallet, chain-id, token-address) when the
 * inputSchema entry carries an explicit `widget` discriminator. Today users
 * are forced to paste raw IDs and addresses for inputs that have richer
 * pickers in the chat-side [[ASK]] flow. Tracked separately so this PR
 * (B3-3603) stays focused on the parser cascade fix.
 */
export function SchemaDrivenForm({
  triggerType,
  triggerPayload,
  inputSchema,
  onRun
}: TriggerArchetypeProps) {
  const config = getArchetypeConfig(triggerType);
  const wrapperKey = config.payloadWrapperKey;
  const scheduleInfo = useMemo(() => {
    if (triggerType === "cronjob" && typeof triggerPayload?.rrule === "string") {
      return formatRRule(triggerPayload.rrule);
    }
    if (triggerType === "schedule" && typeof triggerPayload?.nextRunAt === "string") {
      const date = new Date(triggerPayload.nextRunAt);
      if (!isNaN(date.getTime())) {
        return date.toLocaleString();
      }
    }
    return null;
  }, [triggerType, triggerPayload?.rrule, triggerPayload?.nextRunAt]);
  const fields = useMemo(() => (inputSchema ?? []).filter(input => input.key && input.key.length > 0), [inputSchema]);
  const hasFields = fields.length > 0;

  // Stable signature derived from the field shape (key + type). Used to drive
  // reconciliation effects so we only react to *shape* changes, not to a new
  // array reference passed by the parent on every render.
  const fieldSignature = useMemo(() => fields.map(f => `${f.key}:${f.type}`).join("|"), [fields]);
  const [formValues, setFormValues] = useState<Record<string, string>>(() => initialValuesForFields(fields));

  // Reconcile formValues with the current schema whenever the field shape
  // changes. Drops keys that no longer exist; adds defaults for new keys.
  // User-typed values for keys that survive the schema change are preserved.
  useEffect(() => {
    setFormValues(prev => {
      const next: Record<string, string> = {};
      for (const field of fields) {
        next[field.key] = prev[field.key] ?? defaultStringForType(field.type);
      }
      return next;
    });
    // We intentionally key off fieldSignature (a string), not `fields` (a new
    // array reference on every render), so this only runs on real shape changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldSignature]);
  const [advancedJson, setAdvancedJson] = useState("");
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Stable ref so the emit effect below doesn't need onRun in its deps.
  const onRunRef = useRef(onRun);
  useEffect(() => {
    onRunRef.current = onRun;
  });

  // Emit the staged payload to the modal shell whenever inputs change.
  // Always reads from the *current* fields closure, so even if reconciliation
  // hasn't caught up yet the parent gets the right shape on the next render.
  //
  // The payload is the raw form values (plus any advanced JSON overlay).
  // The backend's run service is responsible for shaping it to match the
  // trigger's resultSchema — it nests under "body" for manual triggers and
  // spreads flat for cronjob/schedule, then writes its own triggeredAt
  // (overwriting any client-supplied value to prevent manipulation). Sending
  // a pre-wrapped { triggeredAt, body } from here causes the backend to
  // wrap again, producing root.result.body.body.<key> at runtime — see
  // services/b3os-workflow/internal/app/api/service/workflow/run.go around
  // line 127.
  useEffect(() => {
    const basePayload: Record<string, unknown> = {};
    for (const field of fields) {
      const raw = formValues[field.key] ?? defaultStringForType(field.type);
      basePayload[field.key] = coerceValue(raw, field.type);
    }
    let advancedOverlay: Record<string, unknown> = {};
    if (advancedJson.trim()) {
      try {
        const parsed = JSON.parse(advancedJson);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          advancedOverlay = parsed as Record<string, unknown>;
        }
      } catch {
        // User is mid-typing — fall through with the base payload only.
      }
    }
    const payload: Record<string, unknown> = {
      ...basePayload,
      ...advancedOverlay
    };
    onRunRef.current(payload);
    // fields is intentionally excluded — fieldSignature already captures shape
    // changes, and depending on `fields` (a new array per render) would emit
    // on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues, advancedJson, fieldSignature]);
  return <div className="space-y-4">
      {scheduleInfo && <div className="border-ds-border-default bg-ds-surface-secondary space-y-2 rounded-lg border p-3">
          <div className="flex items-center gap-2">
            <IconCalendarOutline18 className="text-ds-text-muted h-4 w-4 flex-shrink-0" />
            <span className="body-sm-medium text-ds-text-primary">
              {triggerType === "schedule" ? "Scheduled to run at" : "Scheduled to run"} {scheduleInfo}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <IconCircleInfoOutline18 className="text-ds-text-disabled dark:text-ds-text-muted h-4 w-4 flex-shrink-0" />
            <span className="caption-xs">
              This is an off-schedule test run and won&apos;t affect your scheduled execution.
            </span>
          </div>
        </div>}
      {hasFields ? <div className="divide-ds-neutral-200/70 dark:divide-ds-neutral-800 flex flex-col gap-3 divide-y pb-4">
          {fields.map(field => {
        const value = formValues[field.key] ?? "";
        const isMultiline = field.type === "object" || field.type === "array";
        const isBoolean = field.type === "boolean";
        const header = <div className="mb-1 space-y-0.5">
                <span className="field-label">{field.key}</span>
                {field.description && <p className="body-sm text-ds-text-muted">{field.description}</p>}
              </div>;
        if (isBoolean) {
          return <label key={field.key} className="block">
                  {header}
                  <select value={value || "false"} onChange={e => setFormValues(prev => ({
              ...prev,
              [field.key]: e.target.value
            }))} className="border-ds-neutral-300 text-ds-text-primary dark:border-ds-neutral-700 bg-ds-surface-card w-full rounded-lg border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20">
                    <option value="true">true</option>
                    <option value="false">false</option>
                  </select>
                </label>;
        }
        if (isMultiline) {
          return <div key={field.key}>
                  <JsonInput label={field.key} description={field.description} value={value} onChange={val => setFormValues(prev => ({
              ...prev,
              [field.key]: val
            }))} placeholder={field.type === "array" ? "[]" : "{}"} />
                </div>;
        }
        return <label key={field.key} className="block">
                {header}
                <input type="text" inputMode={field.type === "number" ? "decimal" : "text"} value={value} onChange={e => setFormValues(prev => ({
            ...prev,
            [field.key]: e.target.value
          }))} className="border-ds-neutral-300 text-ds-text-primary placeholder:text-ds-text-disabled dark:border-ds-neutral-700 bg-ds-surface-card w-full rounded-lg border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20" />
              </label>;
      })}
        </div> : <p className="body-sm text-ds-text-muted pb-4">
          No inputs configured. Click the run button below to test the workflow.
        </p>}
      {wrapperKey && <div>
          <button type="button" onClick={() => setShowAdvanced(prev => !prev)} className="label label-xs hover:text-ds-text-secondary flex items-center gap-1.5 transition-colors dark:hover:text-zinc-300">
            <IconChevronDownOutline18 className={`h-3.5 w-3.5 transition-transform ${showAdvanced ? "rotate-180" : "rotate-0"}`} />
            Advanced (payload)
          </button>
          {showAdvanced && <div className="mt-2">
              <JsonInput label="" value={advancedJson} onChange={setAdvancedJson} placeholder='{"key": "value"}' />
              <p className="text-ds-text-disabled mt-1 text-xs">
                Custom keys merged on top of {hasFields ? "the form values above" : "the base payload"}, accessible via{" "}
                <code className="bg-ds-neutral-100 dark:bg-ds-neutral-800 rounded px-1 py-0.5 text-xs">
                  {`{{root.result.${wrapperKey}.<key>}}`}
                </code>
                .
              </p>
            </div>}
        </div>}
    </div>;
}
function initialValuesForFields(fields: Array<{
  key: string;
  type: string;
}>): Record<string, string> {
  const initial: Record<string, string> = {};
  for (const field of fields) {
    initial[field.key] = defaultStringForType(field.type);
  }
  return initial;
}
function defaultStringForType(type: string): string {
  switch (type) {
    case "number":
      return "0";
    case "boolean":
      return "false";
    case "object":
    case "array":
      return "";
    default:
      return "";
  }
}
function coerceValue(raw: string, type: string): unknown {
  switch (type) {
    case "number":
      {
        if (raw === "") return 0;
        const n = Number(raw);
        return isNaN(n) ? 0 : n;
      }
    case "boolean":
      return raw === "true";
    case "object":
      if (!raw.trim()) return {};
      try {
        return JSON.parse(raw);
      } catch {
        return {};
      }
    case "array":
      if (!raw.trim()) return [];
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    default:
      return raw;
  }
}